/**
 * homepage.js
 *
 * Currently contains five independent, section-scoped behaviors:
 *   - Phase 2A: the Hero's interactive shade-card preview (initShadeCard).
 *   - Phase 2D: the Bundle Builder's option-picker radiogroup
 *     (initBundleBuilder).
 *   - Homepage Kits: accessible side buttons for the native scroll rail
 *     (initKitsCarousel).
 *   - Phase 2H: the Social & Community mosaic's global video motion
 *     control (initSocialMotion), added lower in this file.
 *   - Phase 2K correction: the Capture/Newsletter section's post-redirect
 *     success/error presentation (initCaptureNewsletter), added lower in
 *     this file.
 * Each is bound to its own root class and guarded independently, so
 * none depends on or interferes with the others.
 *
 * ---------- Phase 2A: Hero shade-card preview ----------
 * (reference/mvcrochet-demo.html — hover/focus a swatch, its details
 * replace the default hint text in .sc-foot). No reset-on-mouseleave
 * exists in the reference either — the last-shown swatch's info simply
 * stays until another swatch is hovered/focused, matched exactly here.
 *
 * Review correction: the reference's own hover-preview displayed a fixed
 * "9.90 ₪" price on every swatch — not a real Shopify product/variant
 * price. All price semantics have been removed: the second .sc-foot
 * field now shows "גוון {number}" (derived from the swatch's own
 * existing data-n attribute) instead of any currency value.
 *
 * Event delegation (one listener on the grid, not one per swatch) is
 * used both because it's the natural fit for a variable-count block
 * list and because it keeps Theme Editor behavior simple: re-running
 * init() after a section reload only needs to find the (possibly new)
 * grid element once, not rebind 32 individual buttons.
 *
 * mouseover/focusin are used instead of the reference's own
 * mouseenter/focus specifically because those two don't bubble, which
 * would break delegation — mouseover/focusin are the bubbling
 * equivalents and produce the same per-swatch trigger behavior when
 * filtered through closest('.sw').
 *
 * No shopify:section:unload handling is needed here: the only state this
 * file touches lives on the grid element itself (via a bound-once data
 * attribute) and its own listeners, both of which are discarded
 * automatically when Theme Editor removes the section's DOM — unlike
 * assets/drawer.js, nothing here reaches into document-level state that
 * would outlive the section.
 */
(function () {
  'use strict';

  function initShadeCard(root) {
    var grid = root.querySelector('[data-shade-grid]');
    if (!grid || grid.dataset.mvcHomeBound) return;
    grid.dataset.mvcHomeBound = 'true';

    var nameEl = root.querySelector('[data-shade-name]');
    var numberEl = root.querySelector('.sc-foot [data-shade-number]');

    function show(swatch) {
      if (!swatch || !nameEl || !numberEl) return;
      var name = swatch.getAttribute('data-shade-label') || '';
      var number = swatch.getAttribute('data-n') || '';
      if (name) {
        nameEl.textContent = name;
      }
      if (number) {
        numberEl.textContent = 'גוון ' + number;
      }
    }

    grid.addEventListener('mouseover', function (e) {
      var swatch = e.target.closest('.sw');
      if (swatch) show(swatch);
    });
    grid.addEventListener('focusin', function (e) {
      var swatch = e.target.closest('.sw');
      if (swatch) show(swatch);
    });
  }

  /**
   * Phase 2D addition: the Bundle Builder's three-column option picker
   * (reference/mvcrochet-demo.html — click an option in a step to select
   * it, exactly one selected per column at a time).
   *
   * The reference computes a live price total from three hardcoded demo
   * arrays and a fabricated 15% bundle discount — none of that is real
   * Shopify data, so none of it is reproduced here (see
   * home-bundle-builder.liquid's own header comment for the full
   * commerce-classification rationale). What IS reproduced is the
   * selection interaction itself: clicking (or arrow-key navigating) an
   * option marks it selected and updates the matching summary row in the
   * .total panel to echo back the shopper's own choice by name — an
   * accurate reflection of client-side state, not a commercial claim.
   *
   * Implemented as a real radiogroup (role="radiogroup" / role="radio" /
   * aria-checked, roving tabindex) rather than the reference's plain
   * unlabelled buttons, since exactly-one-of-three is inherently
   * radio-button semantics and the reference itself uses semantic
   * <button> elements already (not click-only <div>s) — this only adds
   * the missing ARIA state and keyboard support on top of markup that
   * was already the right element.
   *
   * Root-scoped via [data-bundle-builder] (set on .builder-grid) and
   * bound-once the same way initShadeCard is, for the same Theme Editor
   * re-render reasons. No shopify:section:unload handling is needed for
   * the same reason as initShadeCard: all state lives on the bound
   * element and its own listeners, which are discarded automatically
   * with the section's DOM.
   */
  function initBundleBuilder(root) {
    var builder = root.querySelector('[data-bundle-builder]');
    if (!builder || builder.dataset.mvcHomeBound) return;
    builder.dataset.mvcHomeBound = 'true';

    var groups = builder.querySelectorAll('.opts[data-group]');

    function selectOption(groupEl, optionEl) {
      var options = groupEl.querySelectorAll('.opt');
      options.forEach(function (opt) {
        var selected = opt === optionEl;
        opt.classList.toggle('on', selected);
        opt.setAttribute('aria-checked', selected ? 'true' : 'false');
        opt.setAttribute('tabindex', selected ? '0' : '-1');
      });
      var groupKey = groupEl.getAttribute('data-group');
      var lineValue = builder.querySelector(
        '.trow[data-line="' + groupKey + '"] [data-line-value]'
      );
      if (lineValue) {
        lineValue.textContent = optionEl.getAttribute('data-name') || '';
      }
    }

    groups.forEach(function (groupEl) {
      groupEl.addEventListener('click', function (e) {
        var opt = e.target.closest('.opt');
        if (!opt || !groupEl.contains(opt)) return;
        selectOption(groupEl, opt);
      });

      groupEl.addEventListener('keydown', function (e) {
        if (
          e.key !== 'ArrowDown' &&
          e.key !== 'ArrowUp' &&
          e.key !== 'ArrowLeft' &&
          e.key !== 'ArrowRight' &&
          e.key !== 'Home' &&
          e.key !== 'End'
        ) {
          return;
        }
        var opt = e.target.closest('.opt');
        if (!opt) return;
        var options = Array.prototype.slice.call(groupEl.querySelectorAll('.opt'));
        var idx = options.indexOf(opt);
        if (idx === -1) return;
        var nextIdx = idx;
        // ArrowLeft/ArrowRight are mapped RTL-aware (this project is
        // lang="he" dir="rtl" throughout): ArrowLeft moves toward the end
        // of reading order (next), ArrowRight moves toward its start
        // (previous) — consistent with Up/Down, which are direction-
        // agnostic and always move next/previous respectively.
        if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') nextIdx = (idx + 1) % options.length;
        else if (e.key === 'ArrowUp' || e.key === 'ArrowRight') nextIdx = (idx - 1 + options.length) % options.length;
        else if (e.key === 'Home') nextIdx = 0;
        else if (e.key === 'End') nextIdx = options.length - 1;
        e.preventDefault();
        options[nextIdx].focus();
        selectOption(groupEl, options[nextIdx]);
      });
    });
  }

  /**
   * Phase 2H addition: the Social & Community mosaic's global video
   * motion control (reference/mvcrochet-social.html has no JS of its
   * own at all — this is a production-safety enhancement, not something
   * ported from a reference).
   *
   * `video_tag` in home-social-community.liquid deliberately omits the
   * `autoplay` attribute — this function is the ONLY thing that ever
   * starts or stops those videos, via a single section-level toggle
   * button (rendered only when at least one tile has a real video
   * configured; see the section's own `has_social_video` Liquid
   * variable). With no `autoplay` attribute, a video simply sits at its
   * Shopify-generated poster frame, paused, until this runs — so a
   * failed/blocked/skipped play() attempt never leaves the page in a
   * broken state, only a static one.
   *
   * `aria-pressed="true"` on the toggle means the PAUSED/motion-off
   * state is currently active (aria-label "הפעלת סרטונים" — "play
   * videos", describing the action the next click performs);
   * `aria-pressed="false"` means videos are currently playing
   * (aria-label "השהיית סרטונים" — "pause videos"). `setPausedState()`
   * is the single place that ever writes `aria-pressed`/`aria-label` —
   * CSS alone swaps which of the two always-present SVG icons is
   * visible, keyed off `[aria-pressed]`, so no icon DOM work happens
   * here. The Liquid markup itself always starts `aria-pressed="true"`
   * (paused, Play icon) before any JS runs, so a no-JS page never
   * visually claims playback is active.
   *
   * `playAll()` no longer assumes success: each video's `play()` is
   * wrapped to catch both a synchronous throw and an async Promise
   * rejection, and `playAll()` resolves to whether EVERY video actually
   * started. If even one fails, the caller pauses all of them and
   * reports the paused state — never a mosaic left half-playing with a
   * "playing" control state that doesn't match reality. This applies
   * identically at init (non-reduced-motion path) and on every click.
   *
   * A monotonically incrementing `motionRequestId` guards against stale
   * async resolution: if the user clicks pause while a play() attempt
   * from a previous click (or from init) is still in flight, the id is
   * bumped and pause happens synchronously immediately — when the old
   * attempt's promise eventually resolves, its captured id no longer
   * matches the current one, so it is ignored instead of incorrectly
   * flipping the control back to "playing".
   *
   * Bound-once via the same dataset.mvcHomeBound idiom as
   * initShadeCard/initBundleBuilder, so re-running init() after a Theme
   * Editor section reload is always safe and never double-binds. Videos
   * are queried fresh from `root` (the passed-in .mvc-home-social
   * element) on every call, so a toggle only ever plays/pauses videos
   * inside its own section.
   */
  function initSocialMotion(root) {
    var toggle = root.querySelector('[data-social-motion-toggle]');
    if (!toggle || toggle.dataset.mvcHomeBound) return;
    toggle.dataset.mvcHomeBound = 'true';

    var videos = Array.prototype.slice.call(root.querySelectorAll('.soc-t video'));
    if (!videos.length) return;

    var motionRequestId = 0;

    function safePlay(video) {
      try {
        var playResult = video.play();
        if (playResult && typeof playResult.then === 'function') {
          return playResult.then(
            function () {
              return true;
            },
            function () {
              return false;
            }
          );
        }
        return true;
      } catch (e) {
        return false;
      }
    }

    function playAll() {
      return Promise.all(videos.map(safePlay)).then(function (outcomes) {
        return outcomes.every(function (started) {
          return started;
        });
      });
    }

    function pauseAll() {
      videos.forEach(function (video) {
        video.pause();
      });
    }

    function setPausedState(paused) {
      if (paused) {
        toggle.setAttribute('aria-pressed', 'true');
        toggle.setAttribute('aria-label', 'הפעלת סרטונים');
      } else {
        toggle.setAttribute('aria-pressed', 'false');
        toggle.setAttribute('aria-label', 'השהיית סרטונים');
      }
    }

    function tryPlayAll() {
      var requestId = ++motionRequestId;
      playAll().then(function (allStarted) {
        if (requestId !== motionRequestId) return;
        if (allStarted) {
          setPausedState(false);
        } else {
          pauseAll();
          setPausedState(true);
        }
      });
    }

    var reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      pauseAll();
      setPausedState(true);
    } else {
      tryPlayAll();
    }

    toggle.addEventListener('click', function () {
      var isPaused = toggle.getAttribute('aria-pressed') === 'true';
      if (isPaused) {
        tryPlayAll();
      } else {
        motionRequestId++;
        pauseAll();
        setPausedState(true);
      }
    });
  }

  /**
   * Phase 2K correction addition: the Capture/Newsletter section's
   * post-redirect success/error presentation.
   *
   * home-capture.liquid no longer uses Shopify's Liquid `{% form
   * 'customer' %}` tag at all — this store's actual Shopify runtime
   * rejects that form type (`Invalid form type "customer"`), so the
   * section now submits a real, plain `<form method="post" action=
   * "/contact#NewsletterCapture-{id}">` (Shopify's own documented
   * underlying customer/newsletter POST contract, addressed directly).
   * With no Liquid `form` object available, there is no server-side
   * success/error state to render at all — both regions
   * ([data-capture-success]/[data-capture-error]) start with a plain
   * HTML `hidden` attribute in the Liquid markup itself, so a fresh
   * page load (or any page load that isn't actually a return from THIS
   * section's own submission) always shows neither.
   *
   * This function is pure progressive enhancement, run once per page
   * load: it reads `window.location.hash` and `window.location.search`
   * — nothing else — and decides whether to reveal one of the two
   * already-hidden regions. It never intercepts, prevents, or replaces
   * the actual form submission (no `submit` listener, no
   * `preventDefault`, no fetch/XHR of any kind) — the browser always
   * performs a real, normal POST/redirect exactly as it would with zero
   * JS on the page; this only changes what the NEXT page load shows.
   *
   * The hash check (`location.hash === '#' + card.id`, i.e. `#Newsletter
   * Capture-{id}`) is what scopes this to ONLY this section instance:
   * without it, a page with two Capture sections (or an unrelated
   * contact-style form) could show THIS section's success/error state
   * because a DIFFERENT form on the same page just submitted. Success
   * requires the hash match AND Shopify's own `customer_posted=true`
   * query param; failure requires the hash match, `customer_posted` NOT
   * `'true'`, AND a `form_type=customer` query param (the signal a
   * returned, unsuccessful customer-form submission carries) — anything
   * else (e.g. a plain link to this section with no query string at
   * all) leaves both regions hidden, exactly as they render by default.
   *
   * On success the `<form>` itself is hidden (`form.hidden = true`) and
   * the success region's `hidden` attribute is removed — only the
   * merchant-editable success message is shown. On failure the form
   * stays visible/usable, the error region's `hidden` attribute is
   * removed, and — ONLY in this branch, never on initial render —
   * `aria-invalid`/`aria-describedby` are added to the email input. A
   * returned `contact[email]` query value (if present) is written back
   * with `emailInput.value = ...` (never `innerHTML`, so it is never
   * parsed as markup) so a visitor who mistyped their address doesn't
   * have to retype it. No email validation is reimplemented here: the
   * input's own `type="email"`/`required` attributes are the browser's
   * native validation, unchanged and untouched by this function.
   *
   * Bound-once via the same `dataset.mvcHomeBound` idiom as
   * initShadeCard/initBundleBuilder/initSocialMotion, safe to re-run on
   * every `shopify:section:load`. Reading `location.hash`/`location.
   * search` again on a later re-run (e.g. after a Theme Editor setting
   * change with the same URL still in the address bar) is harmless and
   * idempotent — it can only ever reveal the same already-correct state
   * again, never a different one, since neither the URL nor the bound
   * guard changes between calls for the same page load.
   */
  function initCaptureNewsletter(root) {
    var form = root.querySelector('[data-capture-form]');
    if (!form || form.dataset.mvcHomeBound) return;
    form.dataset.mvcHomeBound = 'true';

    var card = root.querySelector('.capture[id]');
    if (!card) return;

    if (window.location.hash !== '#' + card.id) return;

    var params = new URLSearchParams(window.location.search);
    var posted = params.get('customer_posted') === 'true';
    var wasCustomerForm = params.get('form_type') === 'customer';

    var successEl = root.querySelector('[data-capture-success]');
    var errorEl = root.querySelector('[data-capture-error]');
    var emailInput = form.querySelector('[data-capture-email]');

    if (posted) {
      form.hidden = true;
      if (successEl) successEl.hidden = false;
      return;
    }

    if (wasCustomerForm) {
      if (errorEl) errorEl.hidden = false;
      if (emailInput) {
        emailInput.setAttribute('aria-invalid', 'true');
        if (errorEl && errorEl.id) {
          emailInput.setAttribute('aria-describedby', errorEl.id);
        }
        var emailParam = params.get('contact[email]');
        if (emailParam) {
          emailInput.value = emailParam;
        }
      }
    }
  }

  function initKitsCarousel(root) {
    var carousel = root.querySelector('[data-kits-carousel]');
    var rail = root.querySelector('[data-kits-rail]');
    var leftButton = root.querySelector('[data-kits-scroll="left"]');
    var rightButton = root.querySelector('[data-kits-scroll="right"]');

    if (!carousel || !rail || !leftButton || !rightButton || carousel.dataset.mvcHomeBound) return;
    carousel.dataset.mvcHomeBound = 'true';

    function items() {
      return Array.prototype.slice.call(rail.children);
    }

    function updateControls() {
      var cards = items();
      var hasOverflow = cards.length > 0 && rail.scrollWidth > rail.clientWidth + 2;

      leftButton.hidden = !hasOverflow;
      rightButton.hidden = !hasOverflow;

      if (!hasOverflow) {
        leftButton.disabled = true;
        rightButton.disabled = true;
        return;
      }

      var railRect = rail.getBoundingClientRect();
      var leftMost = Math.min.apply(
        null,
        cards.map(function (card) {
          return card.getBoundingClientRect().left;
        })
      );
      var rightMost = Math.max.apply(
        null,
        cards.map(function (card) {
          return card.getBoundingClientRect().right;
        })
      );

      leftButton.disabled = leftMost >= railRect.left - 2;
      rightButton.disabled = rightMost <= railRect.right + 2;
    }

    function scrollRail(direction) {
      var cards = items();
      if (!cards.length) return;

      var railStyle = window.getComputedStyle(rail);
      var gap = parseFloat(railStyle.columnGap || railStyle.gap) || 16;
      var amount = cards[0].getBoundingClientRect().width + gap;
      var reduceMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      rail.scrollBy({
        left: direction === 'left' ? -amount : amount,
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    }

    leftButton.addEventListener('click', function () {
      scrollRail('left');
    });
    rightButton.addEventListener('click', function () {
      scrollRail('right');
    });

    var updateQueued = false;
    rail.addEventListener(
      'scroll',
      function () {
        if (updateQueued) return;
        updateQueued = true;
        window.requestAnimationFrame(function () {
          updateQueued = false;
          updateControls();
        });
      },
      { passive: true }
    );

    if (window.ResizeObserver) {
      var resizeObserver = new ResizeObserver(updateControls);
      resizeObserver.observe(rail);
    }

    window.requestAnimationFrame(updateControls);
  }

  function init() {
    document.querySelectorAll('.mvc-home').forEach(initShadeCard);
    document.querySelectorAll('.mvc-home-bundle-builder').forEach(initBundleBuilder);
    document.querySelectorAll('.mvc-home-kits').forEach(initKitsCarousel);
    document.querySelectorAll('.mvc-home-social').forEach(initSocialMotion);
    document.querySelectorAll('.mvc-home-capture').forEach(initCaptureNewsletter);
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
